/* import __COLOCATED_TEMPLATE__ from './qa-status.hbs'; */
/* RESPONSIBLE TEAM: team-rd-services */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { cached } from 'tracked-toolbox';
import type IntlService from 'ember-intl/services/intl';
import type Snackbar from 'embercom/services/snackbar';
import type Session from 'embercom/services/session';
import type ConversationTableEntry from 'embercom/objects/inbox/conversation-table-entry';
import { putRequest } from 'embercom/lib/inbox/requests';

export enum QaStatusValue {
  Unreviewed = 'unreviewed',
  NeedsFix = 'needs_fix',
  WontFix = 'wont_fix',
  FixComplete = 'fix_complete',
  PassedReview = 'passed_review',
}

interface Signature {
  Args: {
    conversationEntry: ConversationTableEntry;
  };
}

interface SelectItem {
  text: string;
  value: QaStatusValue;
}

export default class QaStatus extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare snackbar: Snackbar;
  @service declare session: Session;

  @cached
  get qaStatusAttribute() {
    return this.args.conversationEntry.getAttributeForDescriptor('qa_status');
  }

  get status(): QaStatusValue | undefined {
    return this.qaStatusAttribute?.value as QaStatusValue | undefined;
  }

  get displayText(): string {
    let status = this.status;

    if (!status) {
      return this.intl.t('inbox.conversations-table.columns.qaStatusValues.unreviewed');
    }

    switch (status) {
      case QaStatusValue.NeedsFix:
        return this.intl.t('inbox.conversations-table.columns.qaStatusValues.needs_fix');
      case QaStatusValue.WontFix:
        return this.intl.t('inbox.conversations-table.columns.qaStatusValues.wont_fix');
      case QaStatusValue.FixComplete:
        return this.intl.t('inbox.conversations-table.columns.qaStatusValues.fix_complete');
      case QaStatusValue.PassedReview:
        return this.intl.t('inbox.conversations-table.columns.qaStatusValues.passed_review');
      default:
        return this.intl.t('inbox.conversations-table.columns.qaStatusValues.unreviewed');
    }
  }

  get colorClasses(): string {
    let status = this.status;

    switch (status) {
      case QaStatusValue.NeedsFix:
        return 'bg-notice-container text-default';
      case QaStatusValue.WontFix:
        return 'bg-neutral-container text-muted';
      case QaStatusValue.FixComplete:
      case QaStatusValue.PassedReview:
        return 'bg-success-container text-default';
      default:
        return 'bg-neutral-container text-muted';
    }
  }

  get selectItems(): SelectItem[] {
    return [
      {
        value: QaStatusValue.Unreviewed,
        text: this.intl.t('inbox.conversations-table.columns.qaStatusValues.unreviewed'),
      },
      {
        value: QaStatusValue.NeedsFix,
        text: this.intl.t('inbox.conversations-table.columns.qaStatusValues.needs_fix'),
      },
      {
        value: QaStatusValue.WontFix,
        text: this.intl.t('inbox.conversations-table.columns.qaStatusValues.wont_fix'),
      },
      {
        value: QaStatusValue.FixComplete,
        text: this.intl.t('inbox.conversations-table.columns.qaStatusValues.fix_complete'),
      },
      {
        value: QaStatusValue.PassedReview,
        text: this.intl.t('inbox.conversations-table.columns.qaStatusValues.passed_review'),
      },
    ];
  }

  get selectedValue(): QaStatusValue {
    return this.status ?? QaStatusValue.Unreviewed;
  }

  @action
  async handleSelectItem(selectedValue: QaStatusValue): Promise<void> {
    let conversationId = this.args.conversationEntry.id;
    let appId = this.session.workspace.id;
    let attr = this.qaStatusAttribute;
    let previousValue = attr?.value;

    try {
      if (attr) {
        attr.value = selectedValue;
      }

      await putRequest(
        `/ember/conversations/${conversationId}/conversation_attributes/qa_status?app_id=${appId}`,
        {
          value: selectedValue,
        },
      );
    } catch (e: unknown) {
      if (attr) {
        attr.value = previousValue;
      }
      this.snackbar.notifyError(this.intl.t('inbox.conversations-table.errors.qa-status-update'));
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationsTable::TableCells::QaStatus': typeof QaStatus;
    'inbox2/conversations-table/table-cells/qa-status': typeof QaStatus;
  }
}
